import { ButtonBase, styled } from '@mui/material';
import { getActiveAssignmentId } from 'components/Navigation/navigation-utils';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollFadeContainer, Stack, Text, Tooltip } from 'shell/components';

const ASSIGNMENT_LIST_TRANSITION_MS = 180;
const ASSIGNMENT_LIST_MIN_HEIGHT = 96;

const AssignmentNavigationList = ({
    assignments = [],
    expanded,
    onNavigate,
    onScrollableHeightChange,
    open,
    pathname,
    scrollable = true,
}) => {
    const { t } = useTranslation();
    const activeAssignmentId = getActiveAssignmentId(pathname, assignments);
    const activeAssignmentRef = useRef(null);
    const scrollViewportRef = useRef(null);

    useLayoutEffect(() => {
        if (!scrollable || !onScrollableHeightChange) return undefined;

        const updateScrollableHeight = () => {
            const viewport = scrollViewportRef.current;
            if (viewport) onScrollableHeightChange(viewport.clientHeight < ASSIGNMENT_LIST_MIN_HEIGHT);
        };

        updateScrollableHeight();
        const resizeObserver =
            typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateScrollableHeight);
        if (resizeObserver && scrollViewportRef.current) resizeObserver.observe(scrollViewportRef.current);

        return () => resizeObserver?.disconnect();
    }, [onScrollableHeightChange, scrollable]);

    useEffect(() => {
        if (!open) return undefined;

        const scrollActiveAssignment = () =>
            activeAssignmentRef.current?.scrollIntoView?.({ behavior: 'auto', block: 'nearest', inline: 'nearest' });

        const frame = window.requestAnimationFrame(scrollActiveAssignment);
        const transitionTimer = window.setTimeout(scrollActiveAssignment, ASSIGNMENT_LIST_TRANSITION_MS);
        return () => {
            window.cancelAnimationFrame(frame);
            window.clearTimeout(transitionTimer);
        };
    }, [activeAssignmentId, assignments.length, open, scrollable]);

    if (!assignments.length) return null;

    return (
        <AssignmentListExpansion aria-hidden={!open} open={open} scrollable={scrollable}>
            <ScrollFadeContainer scrollable={scrollable} fadeHeight={17} scrollViewportRef={scrollViewportRef}>
                <AssignmentListRoot expanded={expanded} role={'list'}>
                    {assignments.map((assignment) => {
                        const title = assignment.title || t('nav.untitled-assignment', 'Untitled assignment');
                        const active = String(assignment.id) === activeAssignmentId;
                        return (
                            <AssignmentNavigationListItem key={assignment.id} role={'listitem'}>
                                <Tooltip placement={'right'} title={title}>
                                    <AssignmentNavigationButton
                                        aria-current={active ? 'page' : undefined}
                                        aria-label={title}
                                        active={active}
                                        ref={active ? activeAssignmentRef : null}
                                        tabIndex={open ? undefined : -1}
                                        onClick={() => onNavigate(`/assignments/${assignment.id}`)}
                                    >
                                        <Text noWrap color={'inherit'} variant={'p3'}>
                                            {title}
                                        </Text>
                                    </AssignmentNavigationButton>
                                </Tooltip>
                            </AssignmentNavigationListItem>
                        );
                    })}
                </AssignmentListRoot>
            </ScrollFadeContainer>
        </AssignmentListExpansion>
    );
};

const AssignmentListExpansion = styled('div', {
    shouldForwardProp: (prop) => !['open', 'scrollable'].includes(prop),
})(({ open, scrollable, theme }) => ({
    display: 'grid',
    gridTemplateRows: open ? '1fr' : '0fr',
    flex: scrollable ? '1 1 96px' : '0 0 auto',
    minHeight: 0,
    minWidth: 0,
    width: '100%',
    marginTop: open ? theme.spacing(1) : 0,
    overflow: 'hidden',
    visibility: open ? 'visible' : 'hidden',
    pointerEvents: open ? 'auto' : 'none',
    transition: `grid-template-rows 180ms ease-out, margin-top 180ms ease-out, visibility 0s linear ${open ? '0s' : '180ms'}`,
    '& > *': { minHeight: 0, ...(scrollable && { maxHeight: '100%' }) },
    '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
    },
}));

const AssignmentListRoot = styled(Stack)(({ expanded, theme }) => ({
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    alignItems: 'stretch',
    minHeight: ASSIGNMENT_LIST_MIN_HEIGHT,
    gap: theme.spacing(0.5),
    paddingLeft: expanded ? theme.spacing(6) : theme.spacing(1),
    paddingRight: expanded ? theme.spacing(4) : theme.spacing(1),
}));

const AssignmentNavigationListItem = styled('div')({ minWidth: 0 });

const AssignmentNavigationButton = styled(ButtonBase)(({ active, theme }) => ({
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    minHeight: 32,
    padding: theme.spacing(1, 2),
    justifyContent: 'flex-start',
    borderRadius: 8,
    color: active ? theme.palette.ensightful.primary[900] : theme.palette.ensightful.neutral[700],
    background: active ? theme.palette.ensightful.primary[50] : 'transparent',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    '&:hover': {
        color: theme.palette.ensightful.primary[500],
        background: active ? theme.palette.ensightful.primary[50] : 'transparent',
    },
    '&:focus-visible': { outline: `${theme.palette.ensightful.secondary[400]} solid 3px`, outlineOffset: -2 },
    '& > *': { minWidth: 0, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' },
}));

export default AssignmentNavigationList;
